import { useState } from "react";
import { X, Loader2, LogIn, UserPlus, Mail } from "lucide-react";
import { useSiteAuth, signInWithGoogle } from "@/components/GoogleAuth";

// Player sign-in: Google OR email+password. One session cookie identifies any player.
export function PlayerAuthModal({ onClose, title = "Join the Room" }) {
  const { signInWithPassword, registerPlayer } = useSiteAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [age, setAge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (mode === "register") {
        if (!age) throw new Error("Confirm you're 11+ or have a guardian's permission.");
        await registerPlayer(name.trim(), email.trim(), password, age);
      } else {
        await signInWithPassword(email.trim(), password);
      }
      onClose();
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="player-auth-modal">
      <div className="relative w-full max-w-sm bg-[#0E0E12] border border-purple-500/40 rounded-2xl p-7 shadow-[0_0_50px_rgba(168,85,247,0.25)]" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close sign-in" className="absolute top-4 right-4 text-ice/40 hover:text-white transition-colors" data-testid="player-auth-close">
          <X size={18} />
        </button>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fuchsia-400">Player Account</p>
        <h2 className="font-display text-xl font-bold text-white mt-1">{title}</h2>
        <p className="text-xs text-ice/50 mt-1.5">Save XP, levels, streaks, and unlocks across every section.</p>

        <div className="mt-5 grid grid-cols-2 gap-2" data-testid="player-auth-tabs">
          {[["login", "Sign In"], ["register", "Create Account"]].map(([m, label]) => (
            <button key={m} onClick={() => { setMode(m); setError(""); }}
              className={`font-mono text-[10px] uppercase tracking-[0.12em] rounded-full px-3 py-2 border transition-all ${mode === m ? "bg-purple-500 text-white border-purple-500" : "border-white/15 text-ice/55 hover:border-purple-400/60"}`}
              data-testid={`player-auth-tab-${m}`}>
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-4 space-y-3">
          {mode === "register" && (
            <input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} placeholder="Player name"
              className="w-full bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-3 text-sm text-ice focus:outline-none focus:border-purple-400/70"
              data-testid="player-auth-name" />
          )}
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email"
            className="w-full bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-3 text-sm text-ice focus:outline-none focus:border-purple-400/70"
            data-testid="player-auth-email" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={mode === "register" ? 8 : 1}
            placeholder={mode === "register" ? "Password (8+ characters)" : "Password"}
            className="w-full bg-[#14141A] border border-[#2a2a34] rounded-xl px-4 py-3 text-sm text-ice focus:outline-none focus:border-purple-400/70"
            data-testid="player-auth-password" />
          {mode === "register" && (
            <label className="flex items-start gap-2.5 text-[11px] text-ice/55 leading-relaxed cursor-pointer" data-testid="player-auth-age-wrap">
              <input type="checkbox" checked={age} onChange={(e) => setAge(e.target.checked)} className="mt-0.5 accent-purple-500" data-testid="player-auth-age" />
              I'm 11 or older, or I have a parent/guardian's permission to play.
            </label>
          )}
          {error && <p className="text-xs text-red-300" data-testid="player-auth-error">{error}</p>}
          <button type="submit" disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white font-bold text-sm px-6 py-3.5 rounded-full hover:shadow-[0_0_22px_rgba(168,85,247,0.55)] active:scale-[0.98] disabled:opacity-60 transition-all"
            data-testid="player-auth-submit">
            {busy ? <Loader2 size={15} className="animate-spin" /> : (mode === "register" ? <UserPlus size={15} /> : <LogIn size={15} />)}
            {mode === "register" ? "Create My Account" : "Sign In"}
          </button>
        </form>

        <div className="mt-4 flex items-center gap-3">
          <span className="flex-1 h-px bg-white/10" />
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-ice/35">or</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <button onClick={signInWithGoogle}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-white/15 text-ice/80 font-semibold text-sm px-6 py-3 rounded-full hover:border-teal-400/60 hover:text-teal-300 transition-all"
          data-testid="player-auth-google">
          <Mail size={15} /> Continue with Google
        </button>
      </div>
    </div>
  );
}
